// merchantApi.ts
// Thin client wrapper around the /api/merchants/* backend routes.
// Identity is propagated via two custom headers (wallet + email) — the
// backend trusts these alongside the existing API token, same as every
// other /api/* call in this app.

const serverUrl = import.meta.env.VITE_SERVERURL;
const API_SECRET_TOKEN = import.meta.env.VITE_API_SECRET_TOKEN;

// Exported so the webhook hooks/pages can build one-off requests with the
// exact same identity headers as every call in this file.
export function headers(wallet?: string | null, email?: string | null): HeadersInit {
  const h: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${API_SECRET_TOKEN}`,
  };
  if (wallet) h["X-User-Wallet"] = wallet;
  if (email) h["X-User-Email"] = email;
  return h;
}

export interface MerchantBranding {
  logoUrl: string | null;
  brandColor: string;
  accentColor: string;
  footerText: string | null;
  businessNameDisplay: string | null;
}

export interface Merchant {
  id: string;
  businessName: string;
  websiteUrl: string;
  branding: MerchantBranding;
  allowedRedirectDomains: string[];
  allowedPaymentMethods: string[];
  status: string;
  createdAt: any;
}

export interface ApiKeySummary {
  id: string;
  prefix: string;
  label: string;
  createdAt: any;
  lastUsedAt: any;
  revokedAt: any;
}

export interface AccessRequest {
  id: string;
  status: "pending" | "approved" | "rejected";
  businessName: string;
  websiteUrl: string;
  useCase: string;
  createdAt: any;
  reviewedAt: any;
  reviewerNote: string | null;
}

export type MyState =
  | { state: "approved"; merchant: Merchant; keys: ApiKeySummary[] }
  | { state: "pending" | "rejected"; request: AccessRequest }
  | { state: "none" };

export interface ApiErrorShape {
  code: string;
  message: string;
  request_id: string | null;
  // Present on 429 responses only (mirrors the Retry-After header).
  retry_after_seconds?: number;
}

// Shape of every Error thrown by `unwrap` (and by `fetchT` on a client
// timeout). All extras are optional: a network failure is a plain Error.
//   code       → backend error code ("rate_limited", "webhook_url_not_https"…)
//   status     → HTTP status
//   requestId  → "req_…" for support; body first, X-Request-Id header second
//   retryAfter → whole seconds to wait, 429 only
export interface ApiError extends Error {
  code?: string;
  status?: number;
  requestId?: string | null;
  retryAfter?: number;
}

export async function unwrap<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = (body && body.error) as ApiErrorShape | undefined;
    const err: ApiError = new Error(e?.message || `Request failed (${res.status})`);
    err.code = e?.code;
    // The header is CORS-exposed by the backend; it covers responses whose
    // body never made it through (proxy error pages, empty 5xx).
    err.requestId = e?.request_id || res.headers.get("X-Request-Id") || null;
    err.status = res.status;
    const retryAfter =
      Number(e?.retry_after_seconds) || Number(res.headers.get("Retry-After")) || undefined;
    if (retryAfter && retryAfter > 0) err.retryAfter = Math.ceil(retryAfter);
    throw err;
  }
  return body as T;
}

export async function fetchMyMerchant(
  wallet: string | null,
  email: string | null,
): Promise<MyState> {
  const res = await fetch(`${serverUrl}/api/merchants/me`, {
    headers: headers(wallet, email),
  });
  return unwrap<MyState>(res);
}

export async function requestApiAccess(
  wallet: string | null,
  email: string | null,
  payload: {
    businessName: string;
    websiteUrl: string;
    useCase: string;
    monthlyVolumeUsd?: number | null;
    redirectDomains: string[];
  },
): Promise<{
  request: { id: string; status: string; businessName: string; createdAt: any; duplicate: boolean };
}> {
  const res = await fetch(`${serverUrl}/api/merchants/request-access`, {
    method: "POST",
    headers: headers(wallet, email),
    body: JSON.stringify(payload),
  });
  return unwrap(res);
}

export async function updateMerchantSettings(
  wallet: string | null,
  email: string | null,
  patch: Partial<{
    branding: Partial<MerchantBranding>;
    allowedRedirectDomains: string[];
    allowedPaymentMethods: string[];
  }>,
): Promise<{ merchant: Merchant }> {
  const res = await fetch(`${serverUrl}/api/merchants/me/settings`, {
    method: "PATCH",
    headers: headers(wallet, email),
    body: JSON.stringify(patch),
  });
  return unwrap(res);
}

export async function rotateApiKey(
  wallet: string | null,
  email: string | null,
  label?: string,
): Promise<{
  key: { id: string; prefix: string; raw: string };
  message: string;
}> {
  const res = await fetch(`${serverUrl}/api/merchants/me/keys/rotate`, {
    method: "POST",
    headers: headers(wallet, email),
    body: JSON.stringify({ label }),
  });
  return unwrap(res);
}

export async function revokeApiKey(
  wallet: string | null,
  email: string | null,
  keyId: string,
): Promise<{ revoked: boolean }> {
  const res = await fetch(
    `${serverUrl}/api/merchants/me/keys/${encodeURIComponent(keyId)}/revoke`,
    {
      method: "POST",
      headers: headers(wallet, email),
    },
  );
  return unwrap(res);
}

// ────────────────────────────────────────────────────────────────────────────
// Webhooks — /api/merchants/me/webhooks/*
// Same identity headers as above. Every response is camelCase JSON and
// every timestamp is an ISO-8601 UTC string (or null) — unlike the legacy
// /api/merchants/me route, which still returns raw Firestore Timestamps.
// ────────────────────────────────────────────────────────────────────────────

const WH = () => serverUrl + "/api/merchants/me/webhooks";

// fetch with a hard client-side timeout. Used by the two calls that run a
// synchronous delivery attempt on the server (test + resend): those take
// up to ~16 s (DNS + HTTP + body), so a hung connection must not leave a
// modal busy forever. A timeout surfaces as a coded ApiError so the UI
// can tell "we gave up waiting" apart from a real delivery failure.
export async function fetchT(
  url: string,
  init: RequestInit = {},
  ms = 25000,
): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } catch (e) {
    if ((e as Error | undefined)?.name === "AbortError") {
      const err: ApiError = new Error(
        "This is taking longer than expected. Check the delivery history for the result.",
      );
      err.code = "client_timeout";
      throw err;
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

export type WebhookEventType =
  | "checkout.session.completed"
  | "checkout.session.expired"
  | "checkout.session.failed";
export type DeliveryStatus =
  | "pending"
  | "delivering"
  | "retrying"
  | "succeeded"
  | "failed"
  | "skipped";
// Filter value of GET /deliveries?status= (pending/delivering/retrying all
// live in the one "in_progress" bucket).
export type DeliveryBucket = "succeeded" | "failed" | "in_progress" | "skipped";
export type SkipReason =
  | "endpoint_paused"
  | "endpoint_disabled"
  | "endpoint_deleted"
  | "superseded"
  | "backlog_limit";
export type DeliveryTrigger = "event" | "test" | "resend";
export type EndpointHealth = "healthy" | "failing" | "paused" | "disabled" | "idle";
export type AttemptOutcome = "succeeded" | "failed" | "interrupted";
export type CannotResendReason =
  | "in_progress"
  | "endpoint_deleted"
  | "endpoint_disabled"
  | "event_expired"
  | "resend_limit_reached";

export interface WebhookError {
  code: string;
  message: string;
  hint: string | null;
  responseStatus: number | null;
  deliveryId: string;
  eventType: string;
  at: string;
}

// Never contains a signing secret — those come back exactly once, from
// createWebhookEndpoint / rotateWebhookSecret.
export interface WebhookEndpoint {
  id: string;
  url: string;
  urlHost: string;
  description: string | null;
  enabled: boolean;
  events: string[];
  emailAlerts: boolean;
  health: EndpointHealth;
  consecutiveFailures: number;
  failingSince: string | null;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastAttemptAt: string | null;
  lastError: WebhookError | null;
  failedSinceAck: number;
  firstUnackedFailureAt: string | null;
  lastFailedDeliveryId: string | null;
  disabledAt: string | null;
  disabledReason: null | "user" | "auto_failures";
  secretLast4: string;
  secretVersion: number;
  secretCreatedAt: string;
  previousSecretLast4: string | null;
  previousSecretExpiresAt: string | null;
  lastAlertEmail: { kind: string; at: string; ok: boolean; reason: string | null } | null;
  createdAt: string;
  updatedAt: string;
  // Only present when the request asked for `include=stats`.
  stats24h?: { succeeded: number; failed: number; inProgress: number; skipped: number };
}

export interface DeliveryListItem {
  id: string;
  endpointId: string;
  endpointUrl: string;
  eventId: string;
  eventType: string;
  sessionId: string | null;
  livemode: boolean;
  trigger: DeliveryTrigger;
  resendOf: string | null;
  status: DeliveryStatus;
  skipReason: SkipReason | null;
  attemptCount: number;
  maxAttempts: number;
  lastResponseStatus: number | null;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
  lastDurationMs: number | null;
  createdAt: string;
  lastAttemptAt: string | null;
  nextAttemptAt: string | null;
  completedAt: string | null;
  // true = queued > 5 min past its due time (our worker is behind).
  delayed: boolean;
}

export interface DeliveryAttempt {
  number: number;
  startedAt: string;
  durationMs: number | null;
  outcome: AttemptOutcome;
  url: string | null;
  resolvedIp: string | null;
  signatureTimestamp: number | null;
  signature: string | null;
  signedWith: { version: number; last4: string }[];
  responseStatus: number | null;
  responseHeaders: Record<string, string> | null;
  responseSnippet: string | null;
  responseTruncated: boolean;
  errorCode: string | null;
  errorMessage: string | null;
  hint: string | null;
  retryAfterSeconds: number | null;
}

export interface DeliveryDetail extends DeliveryListItem {
  request: {
    method: "POST";
    url: string;
    headers: Record<string, string>;
    // Stored signed string with customer_email masked; null once expired.
    body: string | null;
    // bodyBytes / bodySha256 always describe the TRUE (unmasked) signed bytes.
    bodyBytes: number;
    bodySha256: string;
    bodyMasked: boolean;
    bodyExpired: boolean;
  };
  attempts: DeliveryAttempt[];
  event: { id: string; type: string; created: number; apiVersion: string; livemode: boolean };
  canResend: boolean;
  cannotResendReason: CannotResendReason | null;
  canRetryNow: boolean;
  resends: { id: string; trigger: DeliveryTrigger; status: DeliveryStatus; createdAt: string }[];
}

export interface WebhookAlert {
  // "{endpointId}:{kind}" — together with `since` it is stable for a streak.
  id: string;
  endpointId: string;
  url: string;
  urlHost: string;
  kind: "endpoint_disabled" | "deliveries_failed" | "endpoint_failing";
  severity: "critical" | "warning";
  since: string;
  consecutiveFailures: number;
  failedDeliveries: number;
  lastFailedDeliveryId: string | null;
  lastError: WebhookError | null;
}

// `merchant: false` is a normal 200 for business users without API access
// (the sidebar polls this for every business account) — never an error.
export type WebhookSummary =
  | { merchant: false; badgeCount: 0; alertLevel: "none"; alerts: [] }
  | {
      merchant: true;
      endpointCount: number;
      enabledCount: number;
      failingCount: number;
      disabledCount: number;
      failedDeliveriesUnacked: number;
      alertLevel: "none" | "warning" | "critical";
      badgeCount: number;
      alerts: WebhookAlert[];
      lastDelivery: {
        id: string;
        endpointId: string;
        status: DeliveryStatus;
        eventType: string;
        responseStatus: number | null;
        at: string;
      } | null;
      platform: { secretWritesBlocked: boolean; workerDelayed: boolean };
      generatedAt: string;
    };

export interface DeliveryQuery {
  mode?: "live" | "test";
  endpointId?: string;
  status?: DeliveryBucket;
  eventType?: string;
  sessionId?: string;
  eventId?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}

// Only defined, non-empty values are appended — the backend answers
// 400 invalid_request for a malformed filter, including an empty one.
function qs(params: Record<string, string | number | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export async function fetchWebhookEventTypes(
  wallet: string | null | undefined,
  email: string | null | undefined,
): Promise<{
  apiVersion: string;
  eventTypes: { type: WebhookEventType; description: string; samplePayload: Record<string, unknown> }[];
}> {
  const res = await fetch(`${WH()}/event-types`, { headers: headers(wallet, email) });
  return unwrap(res);
}

export async function fetchWebhookSummary(
  wallet: string | null | undefined,
  email: string | null | undefined,
): Promise<WebhookSummary> {
  const res = await fetch(`${WH()}/summary`, { headers: headers(wallet, email) });
  return unwrap<WebhookSummary>(res);
}

export async function listWebhookEndpoints(
  wallet: string | null | undefined,
  email: string | null | undefined,
  opts?: { includeStats?: boolean },
): Promise<{
  endpoints: WebhookEndpoint[];
  limit: number;
  alertRecipient: string | null;
  platform: { secretWritesBlocked: boolean };
}> {
  const res = await fetch(
    `${WH()}/endpoints${qs({ include: opts?.includeStats ? "stats" : undefined })}`,
    { headers: headers(wallet, email) },
  );
  return unwrap(res);
}

export async function getWebhookEndpoint(
  wallet: string | null | undefined,
  email: string | null | undefined,
  id: string,
  opts?: { includeStats?: boolean },
): Promise<{ endpoint: WebhookEndpoint }> {
  const res = await fetch(
    `${WH()}/endpoints/${encodeURIComponent(id)}${qs({ include: opts?.includeStats ? "stats" : undefined })}`,
    { headers: headers(wallet, email) },
  );
  return unwrap(res);
}

// `secret` is returned here and by rotateWebhookSecret ONLY. Keep it in
// component state; never log it, store it or put it in a URL.
export async function createWebhookEndpoint(
  wallet: string | null | undefined,
  email: string | null | undefined,
  body: {
    url: string;
    description?: string | null;
    events?: string[];
    enabled?: boolean;
    emailAlerts?: boolean;
  },
): Promise<{ endpoint: WebhookEndpoint; secret: string; message: string }> {
  const res = await fetch(`${WH()}/endpoints`, {
    method: "POST",
    headers: headers(wallet, email),
    body: JSON.stringify(body),
  });
  return unwrap(res);
}

// `waitingSkipped` = deliveries that were in progress when the endpoint
// was paused (they will be skipped); 0 for every other kind of patch.
export async function updateWebhookEndpoint(
  wallet: string | null | undefined,
  email: string | null | undefined,
  id: string,
  patch: Partial<{
    url: string;
    description: string | null;
    events: string[];
    enabled: boolean;
    emailAlerts: boolean;
  }>,
): Promise<{ endpoint: WebhookEndpoint; waitingSkipped: number }> {
  const res = await fetch(`${WH()}/endpoints/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: headers(wallet, email),
    body: JSON.stringify(patch),
  });
  return unwrap(res);
}

export async function deleteWebhookEndpoint(
  wallet: string | null | undefined,
  email: string | null | undefined,
  id: string,
): Promise<{ deleted: true }> {
  const res = await fetch(`${WH()}/endpoints/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: headers(wallet, email),
  });
  return unwrap(res);
}

export async function rotateWebhookSecret(
  wallet: string | null | undefined,
  email: string | null | undefined,
  id: string,
  opts?: { revokePreviousNow?: boolean },
): Promise<{
  secret: string;
  secretLast4: string;
  secretVersion: number;
  previousSecretExpiresAt: string | null;
  replacedPreviousSecret: boolean;
  message: string;
}> {
  const res = await fetch(`${WH()}/endpoints/${encodeURIComponent(id)}/rotate-secret`, {
    method: "POST",
    headers: headers(wallet, email),
    body: JSON.stringify({ revokePreviousNow: opts?.revokePreviousNow === true }),
  });
  return unwrap(res);
}

// Resolves with 200 whatever the delivery outcome — a failed test is a
// normal result (`delivery.status === "failed"`), not a thrown error.
export async function sendTestWebhook(
  wallet: string | null | undefined,
  email: string | null | undefined,
  id: string,
  eventType?: WebhookEventType,
): Promise<{ delivery: DeliveryDetail; testsRemaining: number; windowResetsAt: string }> {
  const res = await fetchT(`${WH()}/endpoints/${encodeURIComponent(id)}/test`, {
    method: "POST",
    headers: headers(wallet, email),
    body: JSON.stringify({ eventType }),
  });
  return unwrap(res);
}

export async function listWebhookDeliveries(
  wallet: string | null | undefined,
  email: string | null | undefined,
  q: DeliveryQuery,
): Promise<{ deliveries: DeliveryListItem[]; nextCursor: string | null; hasMore: boolean }> {
  const res = await fetch(
    `${WH()}/deliveries${qs({
      mode: q.mode,
      endpointId: q.endpointId,
      status: q.status,
      eventType: q.eventType,
      sessionId: q.sessionId,
      eventId: q.eventId,
      from: q.from,
      to: q.to,
      limit: q.limit,
      cursor: q.cursor,
    })}`,
    { headers: headers(wallet, email) },
  );
  return unwrap(res);
}

export async function getWebhookDelivery(
  wallet: string | null | undefined,
  email: string | null | undefined,
  id: string,
): Promise<{ delivery: DeliveryDetail }> {
  const res = await fetch(`${WH()}/deliveries/${encodeURIComponent(id)}`, {
    headers: headers(wallet, email),
  });
  return unwrap(res);
}

// Returns the NEW delivery (trigger "resend"), already attempted once.
export async function resendWebhookDelivery(
  wallet: string | null | undefined,
  email: string | null | undefined,
  id: string,
): Promise<{ delivery: DeliveryDetail }> {
  const res = await fetchT(`${WH()}/deliveries/${encodeURIComponent(id)}/resend`, {
    method: "POST",
    headers: headers(wallet, email),
  });
  return unwrap(res);
}

export async function retryWebhookDeliveryNow(
  wallet: string | null | undefined,
  email: string | null | undefined,
  id: string,
): Promise<{ delivery: DeliveryListItem }> {
  const res = await fetch(`${WH()}/deliveries/${encodeURIComponent(id)}/retry-now`, {
    method: "POST",
    headers: headers(wallet, email),
  });
  return unwrap(res);
}

// Omit `endpointId` to acknowledge the failed deliveries of every endpoint.
export async function ackWebhookAlerts(
  wallet: string | null | undefined,
  email: string | null | undefined,
  endpointId?: string,
): Promise<{ acknowledged: number }> {
  const res = await fetch(`${WH()}/alerts/ack`, {
    method: "POST",
    headers: headers(wallet, email),
    body: JSON.stringify({ endpointId }),
  });
  return unwrap(res);
}
